import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container, Paper, Typography, Box, Grid, Button,
    Chip, Divider, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Event as EventIcon,
    Add as AddIcon
} from '@mui/icons-material';
import api from '../services/api';

function ProcessoView() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [processo, setProcesso] = useState(null);
    const [andamentos, setAndamentos] = useState([]);
    const [prazos, setPrazos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadProcesso();
        loadAndamentos();
        loadPrazos();
    }, [id]);

    const loadProcesso = async () => {
        try {
            const response = await api.get(`/processos/${id}`);
            setProcesso(response.data);
        } catch (error) {
            console.error('Erro ao carregar processo:', error);
            setError('Erro ao carregar dados do processo');
        } finally {
            setLoading(false);
        }
    };

    const loadAndamentos = async () => {
        try {
            const response = await api.get(`/andamentos?processo_id=${id}`);
            setAndamentos(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar andamentos:', error);
        }
    };

    const loadPrazos = async () => {
        try {
            const response = await api.get(`/prazos/processo/${id}?ativo=true`);
            setPrazos(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar prazos:', error);
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            return new Date(date).toLocaleDateString('pt-BR');
        } catch {
            return date;
        }
    };

    if (loading) {
        return (
            <Container>
                <Paper sx={{ p: 4, mt: 4 }}>
                    <Typography>Carregando...</Typography>
                </Paper>
            </Container>
        );
    }

    if (error || !processo) {
        return (
            <Container>
                <Paper sx={{ p: 4, mt: 4 }}>
                    <Alert severity="error">{error || 'Processo não encontrado'}</Alert>
                    <Button sx={{ mt: 2 }} onClick={() => navigate('/processos')}>
                        Voltar
                    </Button>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/processos')}
                >
                    Voltar
                </Button>
                <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/processos/editar/${id}`)}
                >
                    Editar
                </Button>
            </Box>

            <Paper sx={{ p: 4, mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Processo: {processo.numero_processo || 'Sem número'}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Cliente</Typography>
                        <Typography variant="body1" gutterBottom>
                            {processo.cliente_nome || '-'} {processo.cliente_cpf ? `(${processo.cliente_cpf})` : ''}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                        <Chip 
                            label={processo.status || 'Sem status'} 
                            color={processo.status?.includes('MORTO') ? 'default' : 'primary'}
                            sx={{ mb: 1 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Tipo de Benefício</Typography>
                        <Typography variant="body1">{processo.tipo_beneficio || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Vara</Typography>
                        <Typography variant="body1">{processo.vara || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Comarca</Typography>
                        <Typography variant="body1">{processo.comarca || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Data de Distribuição</Typography>
                        <Typography variant="body1">{formatDate(processo.data_distribuicao)}</Typography>
                    </Grid>
                    {processo.advogado_nome && (
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">Advogado</Typography>
                            <Typography variant="body1">{processo.advogado_nome}</Typography>
                        </Grid>
                    )}
                    {processo.advogado_externo && (
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">Advogado Externo</Typography>
                            <Typography variant="body1">{processo.advogado_externo}</Typography>
                        </Grid>
                    )}
                    {processo.historico_geral && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">Histórico Geral</Typography>
                            <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {processo.historico_geral}
                                </Typography>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* Seção de Prazos */}
            <Paper sx={{ p: 4, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        <EventIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Prazos ({prazos.length})
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => navigate(`/prazos/novo?processo_id=${id}`)}
                    >
                        Novo Prazo
                    </Button>
                </Box>
                {prazos.length > 0 ? (
                    <TableContainer sx={{ maxHeight: '400px', overflowY: 'auto', position: 'relative' }}>
                        <Table size="small">
                            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                <TableRow>
                                    <TableCell sx={{
                                        fontWeight: 600,
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(25, 25, 25, 0.95)' 
                                            : 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(8px)',
                                        borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                                    }}>
                                        Categoria
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 600,
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(25, 25, 25, 0.95)' 
                                            : 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(8px)',
                                        borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                                    }}>
                                        Tipo
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 600,
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(25, 25, 25, 0.95)' 
                                            : 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(8px)',
                                        borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                                    }}>
                                        Data Vencimento
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 600,
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(25, 25, 25, 0.95)' 
                                            : 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(8px)',
                                        borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                                    }}>
                                        Status
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 600,
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(25, 25, 25, 0.95)' 
                                            : 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(8px)',
                                        borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                                    }}>
                                        Responsável
                                    </TableCell>
                                    <TableCell sx={{
                                        align: 'right',
                                        fontWeight: 600,
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(25, 25, 25, 0.95)' 
                                            : 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(8px)',
                                        borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                                    }}>
                                        Ações
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {prazos.map((prazo) => {
                                    const hoje = new Date();
                                    hoje.setHours(0, 0, 0, 0);
                                    const vencimento = new Date(prazo.data_vencimento);
                                    vencimento.setHours(0, 0, 0, 0);
                                    const diffDays = Math.floor((vencimento - hoje) / (1000 * 60 * 60 * 24));
                                    
                                    let statusColor = 'primary';
                                    let statusLabel = 'Pendente';
                                    if (prazo.status === 'CONCLUIDO') {
                                        statusColor = 'success';
                                        statusLabel = 'Concluído';
                                    } else if (diffDays < 0) {
                                        statusColor = 'error';
                                        statusLabel = 'Vencido';
                                    } else if (diffDays === 0) {
                                        statusColor = 'warning';
                                        statusLabel = 'Vence Hoje';
                                    } else if (diffDays <= 3) {
                                        statusColor = 'warning';
                                        statusLabel = `Vence em ${diffDays} dias`;
                                    }
                                    
                                    return (
                                        <TableRow key={prazo.id}>
                                            <TableCell>{prazo.categoria_nome || '-'}</TableCell>
                                            <TableCell>{prazo.prazo_tipo_nome || '-'}</TableCell>
                                            <TableCell>{formatDate(prazo.data_vencimento)}</TableCell>
                                            <TableCell>
                                                <Chip label={statusLabel} color={statusColor} size="small" />
                                            </TableCell>
                                            <TableCell>
                                                {prazo.responsavel_interno_nome || prazo.responsavel_externo_nome || '-'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    onClick={() => navigate(`/prazos/editar/${prazo.id}`)}
                                                >
                                                    Ver/Editar
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Nenhum prazo cadastrado para este processo.
                    </Typography>
                )}
            </Paper>

            {andamentos.length > 0 && (
                <Paper sx={{ p: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        Andamentos ({andamentos.length})
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <TableContainer sx={{ maxHeight: '400px', overflowY: 'auto', position: 'relative' }}>
                        <Table size="small">
                            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                <TableRow>
                                    <TableCell sx={{
                                        fontWeight: 600,
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(25, 25, 25, 0.95)' 
                                            : 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(8px)',
                                        borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                                    }}>
                                        Data
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 600,
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(25, 25, 25, 0.95)' 
                                            : 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(8px)',
                                        borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                                    }}>
                                        Tipo
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 600,
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(25, 25, 25, 0.95)' 
                                            : 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(8px)',
                                        borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                                    }}>
                                        Descrição
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {andamentos.map((andamento) => (
                                    <TableRow key={andamento.id}>
                                        <TableCell>{formatDate(andamento.data_andamento)}</TableCell>
                                        <TableCell>
                                            <Chip label={andamento.tipo_andamento || 'OUTROS'} size="small" />
                                        </TableCell>
                                        <TableCell>{andamento.descricao}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Container>
    );
}

export default ProcessoView;

